import { useCallback } from 'react';

import { deriveApplicationStatus } from '@/services/policyApplicationStatus';
import {
  getAssetCareApplicationUnavailableMessage,
  isAssetCareApplicationUnavailableForState,
} from '@shared';

type AvailabilityInput = {
  policyFundingSource?: PolicyFundingSource;
  existingApplication?: ClientPolicyApplication;
  applicationStatus?: ApplicationStatus;
  stateOfResidenceAbbreviation?: string;
  selectedState?: string;
  isStateSelectionVisible: boolean;
};

export type AssetCareApplicationAvailability = {
  state: string | undefined;
  isUnavailable: boolean;
  message: string | null;
};

export function resolveAssetCareApplicationAvailability({
  policyFundingSource,
  existingApplication,
  applicationStatus,
  stateOfResidenceAbbreviation,
  selectedState,
  isStateSelectionVisible,
}: AvailabilityInput): AssetCareApplicationAvailability {
  const resolvedStatus = existingApplication
    ? deriveApplicationStatus(existingApplication).status
    : applicationStatus || 'not_created';
  const persistedState =
    existingApplication?.stateOfResidenceAbbreviation || stateOfResidenceAbbreviation;
  const state =
    resolvedStatus === 'not_started'
      ? persistedState || undefined
      : resolvedStatus === 'not_created' && isStateSelectionVisible
        ? selectedState || undefined
        : undefined;
  const isUnavailable = Boolean(
    policyFundingSource && isAssetCareApplicationUnavailableForState(policyFundingSource, state),
  );

  return {
    state,
    isUnavailable,
    message: isUnavailable && state ? getAssetCareApplicationUnavailableMessage(state) : null,
  };
}

export function useAssetCareApplicationAvailability() {
  const resolveAvailability = useCallback(resolveAssetCareApplicationAvailability, []);
  const guardApplicationAction = useCallback(
    (availability: AssetCareApplicationAvailability, action: () => void) => {
      if (availability.isUnavailable) return false;
      action();
      return true;
    },
    [],
  );

  return { resolveAvailability, guardApplicationAction };
}
